import React, { useState } from 'react';
import Breadcrumb from '../../components/breadcrumb';
import UQFileRead from '../../components/file-read';
import UQSpacer from '../../components/spacer';
import UQCode from '../../components/code';
import UQCard from '../../components/card';
import UQDataGrid, { UQDataGridColumn } from '../../components/data-grid';
import { BREADCRUMB_ROOT } from '../../common/config';

const attributes = [
  {
    attribute: 'onFileRead',
    type: '(file: File, content: string | ArrayBuffer | null) => void',
    default: 'undefined',
    description: 'Callback function called when a file is read.',
  },
  {
    attribute: 'accept',
    type: 'string',
    default: 'undefined',
    description: 'Accepted file types (e.g., ".txt, .csv, application/json").',
  },
  {
    attribute: 'multiple',
    type: 'boolean',
    default: 'false',
    description: 'Allow selection of multiple files.',
  },
  {
    attribute: 'label',
    type: 'string',
    default: 'undefined',
    description: 'Label for the file input button.',
  },
  {
    attribute: 'className',
    type: 'string',
    default: "''",
    description: 'Additional class names for custom styling.',
  },
];

const attributeColumns: UQDataGridColumn<typeof attributes[0]>[] = [
  {
    columnId: 'attribute',
    renderHeaderCell: () => 'Attribute',
    renderCell: (item) => item.attribute,
  },
  {
    columnId: 'type',
    renderHeaderCell: () => 'Type',
    renderCell: (item) => <code>{item.type}</code>,
  },
  {
    columnId: 'default',
    renderHeaderCell: () => 'Default',
    renderCell: (item) => <code>{item.default}</code>,
  },
  {
    columnId: 'description',
    renderHeaderCell: () => 'Description',
    renderCell: (item) => item.description,
  },
];

const FileReadDemoPage = () => {
  const [fileContent, setFileContent] = useState<string | ArrayBuffer | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  return (
    <>
      <Breadcrumb crumbs={[BREADCRUMB_ROOT, 'File Read']} />
      <h1>UQFileRead Demo</h1>
      <UQSpacer size="m" />

      {/* Description */}
      <h2>Description</h2>
      <p>
        The <code>UQFileRead</code> component allows users to select and read files from their device. It supports custom file types, multiple file selection, and provides the file content via a callback.
      </p>
      <UQSpacer size="m" />

      {/* Attributes Table */}
      <h2>Attributes</h2>
      <UQSpacer size="s" />
      <UQDataGrid
        items={attributes}
        columns={attributeColumns}
        sortable={false}
        selectionMode="none"
        rowLines
      />
      <UQSpacer size="m" />

      {/* Demo */}
      <h2>Live Demo</h2>
      <UQSpacer size="s" />
      <UQCard header="File Read Example">
        <UQFileRead
          label="Select a file"
          accept=".txt,.json"
          onFileRead={(file, content) => {
            setFileName(file.name);
            setFileContent(typeof content === 'string' ? content : '[Binary Data]');
          }}
        />
        <UQSpacer size="s" />
        {fileName && (
          <div>
            <strong>File:</strong> {fileName}
            <UQSpacer size="xs" />
            <strong>Content:</strong>
            <pre style={{ maxHeight: 200, overflow: 'auto', background: '#222', color: '#fff', padding: 8 }}>
              {typeof fileContent === 'string'
                ? fileContent
                : fileContent instanceof ArrayBuffer
                  ? new TextDecoder().decode(fileContent)
                  : ''}
            </pre>
          </div>
        )}
      </UQCard>
      <UQSpacer size="m" />

      {/* Code Example */}
      <h2>Code Example</h2>
      <UQCode
        code={`<UQFileRead
  label="Select a file"
  accept=".txt,.json"
  onFileRead={(file, content) => {
  }}
/>`}
        enableCopy
      />
    </>
  );
};

export default FileReadDemoPage;